import React, { useCallback, useEffect } from 'react';
import { useMatch, useParams } from 'react-router-dom';
import { useTrackApplicationAnalytics } from '@app/api';
import { useTrackingParams } from '@app/analytics';

const FieldIdContext = React.createContext<(fieldIs: string) => void>(() => {});

interface Props {
  children: React.ReactNode;
  sessionId?: string;
  sessionLinkId?: string;
}
export const TrackApplicationContainer = ({
  sessionId,
  sessionLinkId,
  children,
}: Props) => {
  const [fieldIdState, saveFieldId] = React.useState<string | undefined>(
    undefined,
  );

  const { mutate: track } = useTrackApplicationAnalytics(sessionId);
  const tracking = useTrackingParams();
  const source = tracking.utm_source;

  const matchRoot = useMatch('/s/:id/');
  const matchS = useMatch('/s/:id/:page');
  const matchA = useMatch('/s/:id/a/:id/:page');
  const matchP = useMatch('/p/:id/booking');
  const matchPA = useMatch('/p/:id/booking/a/:id/:page');

  let step: string | undefined = undefined;
  if (matchRoot || matchP) {
    step = 'home';
  } else if (matchS || matchA || matchPA) {
    const match = matchS || matchA || matchPA;
    step = match!.params.page;
  }

  useEffect(() => {
    if (!step) {
      return;
    }

    let fieldId = fieldIdState;
    if (step !== 'screening') {
      fieldId = undefined;
    }

    const key = ['track', sessionId, step, fieldId, source, sessionLinkId].join(
      ':',
    );
    const now = new Date().getTime();

    const time = Number(sessionStorage.getItem(key) || 0);
    sessionStorage.setItem(key, String(now));

    if (now - time < 1000 * 30) {
      // 30 sec
      return;
    }

    track({
      step,
      fieldId,
      sessionLinkId,
      source,
    });
  }, [fieldIdState, step, track, sessionLinkId, source]);

  return (
    <FieldIdContext.Provider value={saveFieldId}>
      {children}
    </FieldIdContext.Provider>
  );
};

export const useTrackFieldId = () => {
  const saveFieldId = React.useContext(FieldIdContext);
  return useCallback(
    (fieldId?: string) => {
      if (!fieldId) {
        return;
      }
      saveFieldId(fieldId);
    },
    [saveFieldId],
  );
};
